import { furthest, RGBColor } from 'color-diff';
import { i18n } from 'i18next';
import { IAppSetting, Language } from '../interfaces';
import type { IBaseFile } from '../interfaces/defaultSchema';
import {
  ApolloCache,
  DefaultContext,
  gql,
  MutationUpdaterFunction,
  OperationVariables,
} from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { getRequestError } from './requestError';
import { ApolloContextKey } from '../enums/ApolloContextKey';
import {
  queryRequireTenantId,
  updateCacheHandlerByName,
} from '../services/graphql/cache';
import { Query } from '../services/graphql/query';
import { parse, stringify } from 'flatted';
import { LocalStorageKeys } from '../enums/EnumsValues';
import { TrackedQuery } from '../hooks/apollo/ApolloProviderWrapper';

export class InternalError extends Error {
  isInternal: true;

  constructor(message: string) {
    super(message);
    this.isInternal = true;
  }
}

export const MAX_SIZE_IMAGE_IN_MB = 10;
export const PREFIX_LEGAL_NUMBER = 20110;
export const SECONDS_CONSTANT = 1000;

const Tools = () => {};

interface CurrencyFormatOptions {
  locale?: string;
  currency?: string;
  minimumFractionDigits?: number;
}

/** Format a currency value with configurable locale and currency defaults. */
Tools.formatCurrency = (
  val: unknown,
  {
    locale = 'es-AR',
    currency = 'ARS',
    minimumFractionDigits = 0,
  }: CurrencyFormatOptions = {},
): string => {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    style: 'currency',
    currency,
  }).format(Number(val));
};

/**
 * Traduce el valor de ordenamiento de TablePro al valor que necesita el backend
 * @param data
 */
Tools.getTypeOrderByTableSortParam = (val: string) => {
  return val === 'ascend' ? 'asc' : 'desc';
};

/**
 * Retorna ArrayBuffer de un base64
 * @param base64
 */
Tools.base64ToArrayBuffer = (base64: string) => {
  const binaryString = window.atob(base64); // Comment this if not using base64
  const bytes = new Uint8Array(binaryString.length);
  return bytes.map((_byte, i) => binaryString.codePointAt(i) ?? 0);
};

/**
 * Retorna URL de un Blob
 * @param file
 */
Tools.createObjectURLOfBlob = (file: { img: string; mimetype: string }) => {
  const arrayBuffer = Tools.base64ToArrayBuffer(file.img);
  const blob = new Blob([arrayBuffer], { type: file.mimetype });
  const fileURL: string = URL.createObjectURL(blob);
  return fileURL;
};

/**
 * Descarga archivo PDF
 * @param file
 */
Tools.downloadFile = (file: IBaseFile | undefined) => {
  if (!file?.file) return;
  const createAndDownloadBlobFile = (
    arrayBuffer: Uint8Array,
    filename: string,
  ) => {
    const fileBuffer = arrayBuffer.buffer.slice(
      arrayBuffer.byteOffset,
      arrayBuffer.byteOffset + arrayBuffer.byteLength,
    ) as ArrayBuffer;
    const blob = new Blob([fileBuffer]);
    const fileName = `${filename}`;
    const legacyNavigator = navigator as Navigator & {
      msSaveBlob?: (blob: Blob, filename: string) => boolean;
    };
    if (legacyNavigator.msSaveBlob) {
      // IE 10+
      legacyNavigator.msSaveBlob(blob, fileName);
    } else {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // eslint-disable-next-line no-useless-escape
      link.download = file.filename.replace(/^.*[\\\/]/, '');
      link.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const arrayBuffer = Tools.base64ToArrayBuffer(file.file);
  createAndDownloadBlobFile(arrayBuffer, file.filename);
};

/**
 * Descarga archivo de imagen (png - jpeg - gif)
 * @param image
 */
Tools.downloadImage = (image: {
  mimetype: string;
  img: string;
  filename: string;
}) => {
  const link = document.createElement('a');
  link.href = `data:${image.mimetype};base64,${image.img}`;
  link.download = image.filename;
  link.click();
};

Tools.filter = <T,>(list: T[], search: string): T[] => {
  if (search) {
    const result = list.filter((item) => {
      const itemLower = (JSON.stringify(item) ?? '').toLowerCase();
      const searchSplit = search.toLowerCase().split(' ');
      let validate = 0;
      searchSplit.forEach((search) => {
        if (itemLower.includes(search)) {
          validate += 1;
        }
      });
      if (validate === searchSplit.length) {
        return true;
      } else {
        return false;
      }
    });
    return result;
  } else {
    return list;
  }
};

Tools.fileWeightOfSize = (size: number): number => {
  //lo paso a kbyte
  const kbyte = size / 1000;
  //lo paso a megas
  const mega = kbyte / 1000;
  //redondeo a un decimal
  return Number(mega.toFixed(2));
};

Tools.capitalize = (text: string): string => {
  if (text.length > 1) {
    const firstCharacter = text.charAt(0);
    return `${firstCharacter.toUpperCase()}${text.substring(1)}`;
  }
  return text;
};

Tools.stripPaginationVariables = <T extends Record<string, unknown>>(
  variables: T,
): Omit<T, 'skip' | 'take'> => {
  const returningVariables = { ...variables };
  delete returningVariables.skip;
  delete returningVariables.take;
  return returningVariables;
};

Tools.getSkip = (pageSize: number | undefined, current: number | undefined) => {
  if (!pageSize || !current) {
    return 0;
  }
  return pageSize * (current - 1);
};

Tools.blobToUrlBase64 = (blob: Blob) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

Tools.sortByZone = (a: { label: string }, b: { label: string }) => {
  const [ahh, amm] = a.label.split('GMT')[1].split(')')[0].split(':');
  const [bhh, bmm] = b.label.split('GMT')[1].split(')')[0].split(':');
  return Number(ahh) * 60 + Number(amm) - (Number(bhh) * 60 + Number(bmm));
};
Tools.isDefined = <T,>(value: T | null | undefined): value is T =>
  value !== null && value !== undefined;

Tools.getBase64WithCallback = (
  img: Blob | string,
  callback: (result: string) => void,
) => {
  if (!(img instanceof Blob)) return;
  const reader = new FileReader();
  reader.addEventListener('load', () => {
    if (typeof reader.result === 'string') callback(reader.result);
  });
  reader.readAsDataURL(img);
};

interface IGetUrlOfBase64FileParameter {
  mimetype: string;
  fileBase64: string;
}

Tools.getUrlOfBase64File = (fileData: IGetUrlOfBase64FileParameter): string => {
  return `data:${fileData.mimetype};base64,${fileData.fileBase64}`;
};

Tools.hexToRgb = (hexColor: string): RGBColor => {
  const hex = hexColor.startsWith('#') ? hexColor.substring(1, 7) : hexColor;
  return {
    R: Number.parseInt(hex.slice(0, 2), 16),
    G: Number.parseInt(hex.slice(2, 4), 16),
    B: Number.parseInt(hex.slice(4, 6), 16),
  };
};

Tools.rgbToHex = (rgbColor: RGBColor): string => {
  const { R, G, B } = rgbColor;
  return `#${[R, G, B]
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')}`;
};

Tools.getFontColorWithContrast = (
  bgColor: string,
  lightColor: string = '#ffffff',
  darkColor: string = '#000000',
): string => {
  const normalizedColor = bgColor.trim();
  if (!/^#(?:[\da-f]{3}|[\da-f]{6})$/i.test(normalizedColor)) {
    return darkColor;
  }

  const hex =
    normalizedColor.length === 4
      ? `#${normalizedColor[1]}${normalizedColor[1]}${normalizedColor[2]}${normalizedColor[2]}${normalizedColor[3]}${normalizedColor[3]}`
      : normalizedColor;
  const color = Tools.hexToRgb(hex);
  const palette = [Tools.hexToRgb(lightColor), Tools.hexToRgb(darkColor)];
  const bestMatch = furthest(color, palette);
  return Tools.rgbToHex(bestMatch);
};

Tools.getSettingDescriptionTranslation = (
  setting: IAppSetting,
  i18n: i18n,
  languages: Language[],
) => {
  const language_id = languages.find(
    (lang) => lang.language_code === i18n.language,
  )?.id;
  if (!language_id) return setting.description || '';

  const translation = setting.app_setting_translation?.find(
    (translation) => translation.language_id === language_id,
  )?.description;

  return translation || setting.description || '';
};

Tools.insertElementInSpecificPositionOfArray = <T,>(data: {
  array: T[];
  element: T;
  position: number;
}): T[] => {
  const { array = [], element } = data;
  let position = data.position;

  if (position < 0) position = 0;
  if (position > array.length) position = array.length;

  return array.slice(0, position).concat(element).concat(array.slice(position));
};

interface LocalStorageSchema {
  [LocalStorageKeys.optimisticValuesToReplace]: (
    | [number, number]
    | [string, string]
  )[];
  [LocalStorageKeys.TrackedQueries]: TrackedQuery[];
  [LocalStorageKeys.Token]: string;
  [LocalStorageKeys.UserId]: number;
  [LocalStorageKeys.TwoFactorValidated]: boolean;
  [LocalStorageKeys.TwoFAOmitted]: string;
  [LocalStorageKeys.LastTenantId]: string;
  [LocalStorageKeys.googleImage]: string;
}

/**
 * To define whether an object is plain or has circular references
 */
const isPlain: { [key in keyof LocalStorageSchema]: boolean } = {
  [LocalStorageKeys.optimisticValuesToReplace]: true,
  [LocalStorageKeys.TrackedQueries]: false,
  [LocalStorageKeys.Token]: true,
  [LocalStorageKeys.UserId]: true,
  [LocalStorageKeys.TwoFactorValidated]: true,
  [LocalStorageKeys.TwoFAOmitted]: true,
  [LocalStorageKeys.LastTenantId]: true,
  [LocalStorageKeys.googleImage]: true,
};

/**
 * Función de escritura tipada del local storage
 */
Tools.setStorage = <T extends keyof LocalStorageSchema>(
  key: T,
  value: LocalStorageSchema[T],
): void => {
  if (isPlain[key]) {
    localStorage.setItem(key, JSON.stringify(value));
  } else {
    localStorage.setItem(key, stringify(value));
  }
};

/**
 * Función de lectura tipada del local storage
 */
Tools.getStorage = <T extends keyof LocalStorageSchema>(
  key: T,
): LocalStorageSchema[T] | null => {
  const value = localStorage.getItem(key);
  if (!value) return null;
  if (isPlain[key]) {
    return JSON.parse(value) as LocalStorageSchema[T];
  } else {
    return parse(value) as LocalStorageSchema[T];
  }
};

/**
 * Reemplaza dentro de un objeto todas las ocurrencias de un determinado valor por otro. Ambos deben ser del mismo tipo
 */
Tools.replaceValuesInObj = <T extends string | number>(
  o: object,
  targetValue: T,
  replaceValue: T,
) => {
  const record = o as Record<string, unknown>;
  Object.keys(record).forEach((key) => {
    if (record[key] === targetValue) record[key] = replaceValue;
    if (typeof record[key] === 'object' && record[key] !== null) {
      Tools.replaceValuesInObj(
        record[key] as object,
        targetValue,
        replaceValue,
      );
    }
  });
};

Tools.isGraphqlError = (error: unknown) => {
  return (
    CombinedGraphQLErrors.is(error) ||
    (typeof error === 'object' &&
      error !== null &&
      ('status_code' in error || 'statusCode' in error))
  );
};

/**
 * Generador de contexto estándar de una mutación trackeada
 */
Tools.trackedContext = (
  serializationKey: (string | number)[],
  requestName: string,
) => ({
  tracked: true,
  serializationKey: serializationKey.map((v) => v.toString()),
  [ApolloContextKey.CUSTOM_OPERATION_NAME]: requestName,
});

/**
 * Actualiza la cache en base a las funciones definidas para cada mutacion, y su rollback en caso de error
 */
const isOptimisticRerun = (
  mutationResult: unknown,
  context: DefaultContext,
): boolean =>
  typeof mutationResult === 'object' &&
  mutationResult !== null &&
  'isOptimistic' in mutationResult &&
  mutationResult.isOptimistic === true &&
  Boolean(context.rerun);

const getTenantId = (query: string, variables: OperationVariables): unknown =>
  queryRequireTenantId.includes(query)
    ? (variables?.tenant_id ?? variables?.input?.tenant_id)
    : undefined;

const updateSingleCachedQuery = (
  cache: ApolloCache,
  queryName: string,
  update: (
    data: unknown,
    context: { mutationResult: { data: unknown } },
  ) => unknown,
  mutationData: Record<string, unknown>,
  variables: OperationVariables,
): void => {
  if (!(queryName in Query)) return;

  const tenantId = getTenantId(queryName, variables);
  const gqlQuery = gql(Query[queryName as keyof typeof Query].gql);
  try {
    cache.updateQuery(
      {
        query: gqlQuery,
        variables: tenantId ? { tenant_id: tenantId } : undefined,
      },
      (data: unknown) => {
        const dataRecord =
          typeof data === 'object' && data !== null && !Array.isArray(data)
            ? (data as Record<string, unknown>)
            : undefined;
        return update(dataRecord, { mutationResult: { data: mutationData } });
      },
    );
  } catch (error) {
    console.error(error);
  }
};

const updateCache: MutationUpdaterFunction<
  Record<string, unknown>,
  OperationVariables,
  ApolloCache
> = (cache, { data: mutationData }, { context, variables }) => {
  if (!mutationData) return;

  const operationName = context?.[ApolloContextKey.CUSTOM_OPERATION_NAME];
  if (!operationName) return;

  const mutationResult = mutationData[operationName];
  if (isOptimisticRerun(mutationResult, context ?? {})) return;

  const cacheUpdates =
    updateCacheHandlerByName[
      operationName as keyof typeof updateCacheHandlerByName
    ];
  if (!cacheUpdates) return;

  for (const [queryName, update] of Object.entries(cacheUpdates)) {
    updateSingleCachedQuery(cache, queryName, update, mutationData, variables);
  }
};

Tools.updateCache = updateCache;

Tools.getOptimisticResponse = (context: DefaultContext) => {
  return context.optimisticResponse ?? context._optimisticResponse;
};

Tools.getRandomNumber = () => {
  return Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);
};

Tools.processQueryError = (originalError: unknown) => {
  const error = getRequestError(originalError);
  if (error instanceof InternalError) {
    console.error(error);
  } else {
    window.dispatchEvent(
      new CustomEvent('app:message-error', {
        detail: { message: error.message },
      }),
    );
  }
};

export { Tools };
